import { useNavigate, useParams } from '@tanstack/react-router';
import { useEffect, useState } from 'react';

import { apiClient } from '../api/client.js';
import { useUpdateBotFlow } from '../api/queries.js';
import { useAuthStore } from '../auth/auth-store.js';

import './bot-flow.css';

type NodeType = 'message' | 'input' | 'buttons' | 'condition' | 'api_call' | 'transfer' | 'end';

interface BotNode {
  type: NodeType;
  text?: string;
  next?: string;
  save_as?: string;
  buttons?: Array<{ label: string; value: string; next?: string }>;
  if?: { var: string; op: string; val?: unknown };
  then?: string;
  else?: string;
  method?: string;
  url?: string;
  [k: string]: unknown;
}

interface BotFlow {
  start: string;
  nodes: Record<string, BotNode>;
}

export function BotFlowPage(): JSX.Element {
  const slug = useAuthStore((s) => s.user?.workspaceSlug);
  const params = useParams({ strict: false }) as { scenarioId?: string };
  const navigate = useNavigate();
  const update = useUpdateBotFlow(slug);

  const [scenario, setScenario] = useState<{ id: string; name: string; flowJson: BotFlow } | null>(
    null,
  );
  const [activeNodeId, setActiveNodeId] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (!slug || !params.scenarioId) return;
    void apiClient
      .get<{
        id: string;
        name: string;
        flowJson: BotFlow;
      }>(`/bot-scenarios/${params.scenarioId}`, { workspaceSlug: slug })
      .then((s) => {
        setScenario(s);
        setActiveNodeId(s.flowJson.start);
      });
  }, [slug, params.scenarioId]);

  if (!scenario) return <div style={{ padding: 16 }}>Загрузка…</div>;

  const flow = scenario.flowJson;
  const nodeIds = Object.keys(flow.nodes);
  const activeNode = activeNodeId ? flow.nodes[activeNodeId] : null;

  function updateNode(id: string, patch: Partial<BotNode>): void {
    setScenario((s) => {
      if (!s) return s;
      const next: BotFlow = {
        ...s.flowJson,
        nodes: { ...s.flowJson.nodes, [id]: { ...s.flowJson.nodes[id], ...patch } as BotNode },
      };
      return { ...s, flowJson: next };
    });
  }

  function addNode(): void {
    const id = `node_${nodeIds.length + 1}`;
    setScenario((s) => {
      if (!s) return s;
      const next: BotFlow = {
        ...s.flowJson,
        nodes: { ...s.flowJson.nodes, [id]: { type: 'message', text: 'Новое сообщение' } },
      };
      return { ...s, flowJson: next };
    });
    setActiveNodeId(id);
  }

  function deleteNode(id: string): void {
    if (id === flow.start) {
      setError('Нельзя удалить стартовый узел');
      return;
    }
    setScenario((s) => {
      if (!s) return s;
      const { [id]: _, ...rest } = s.flowJson.nodes;
      return { ...s, flowJson: { ...s.flowJson, nodes: rest } };
    });
    setActiveNodeId(flow.start);
  }

  async function save(): Promise<void> {
    if (!scenario) return;
    setError(null);
    try {
      await update.mutateAsync({ id: scenario.id, flow: scenario.flowJson });
    } catch (err) {
      setError((err as Error).message);
    }
  }

  return (
    <div className="inbx-flow">
      <header className="inbx-flow__header">
        <button
          type="button"
          onClick={() => navigate({ to: '/bots' })}
          style={{ background: 'transparent', border: 'none' }}
        >
          ← Назад
        </button>
        <h1>{scenario.name}</h1>
        <div style={{ marginLeft: 'auto', display: 'flex', gap: 8 }}>
          <button type="button" onClick={addNode}>
            + Узел
          </button>
          <button type="button" onClick={save} disabled={update.isPending}>
            {update.isPending ? '…' : 'Сохранить'}
          </button>
        </div>
      </header>

      {error && <div className="inbx-flow__error">{error}</div>}

      <div className="inbx-flow__body">
        <aside className="inbx-flow__nodes">
          <div className="inbx-flow__nodes-title">Узлы ({nodeIds.length})</div>
          <ul>
            {nodeIds.map((id) => (
              <li
                key={id}
                className={'inbx-flow__node' + (id === activeNodeId ? ' is-active' : '')}
                onClick={() => setActiveNodeId(id)}
                draggable
                onDragStart={(e) => {
                  e.dataTransfer.setData('text/plain', id);
                  e.dataTransfer.effectAllowed = 'move';
                }}
                onDragOver={(e) => {
                  e.preventDefault();
                  e.dataTransfer.dropEffect = 'move';
                }}
                onDrop={(e) => {
                  e.preventDefault();
                  const fromId = e.dataTransfer.getData('text/plain');
                  if (!fromId || fromId === id) return;
                  setScenario((s) => {
                    if (!s) return s;
                    const order = nodeIds.filter((x) => x !== fromId);
                    const targetIdx = order.indexOf(id);
                    order.splice(targetIdx, 0, fromId);
                    const reorderedNodes: Record<string, BotNode> = {};
                    for (const k of order) reorderedNodes[k] = s.flowJson.nodes[k]!;
                    return { ...s, flowJson: { ...s.flowJson, nodes: reorderedNodes } };
                  });
                }}
              >
                <strong>{id}</strong>
                <span className="inbx-flow__node-type">{flow.nodes[id]?.type}</span>
                {id === flow.start && <span className="inbx-flow__start">▶ START</span>}
                <span className="inbx-flow__drag-handle" title="Перетащить для изменения порядка">
                  ⋮⋮
                </span>
              </li>
            ))}
          </ul>
        </aside>

        <main className="inbx-flow__editor">
          {activeNode && activeNodeId ? (
            <NodeEditor
              id={activeNodeId}
              node={activeNode}
              allNodes={nodeIds}
              isStart={activeNodeId === flow.start}
              onUpdate={(patch) => updateNode(activeNodeId, patch)}
              onDelete={() => deleteNode(activeNodeId)}
              onSetStart={() =>
                setScenario((s) =>
                  s ? { ...s, flowJson: { ...s.flowJson, start: activeNodeId } } : s,
                )
              }
            />
          ) : (
            <div className="inbx-empty">Выберите узел</div>
          )}
        </main>
      </div>
    </div>
  );
}

function NodeEditor({
  id,
  node,
  allNodes,
  isStart,
  onUpdate,
  onDelete,
  onSetStart,
}: {
  id: string;
  node: BotNode;
  allNodes: string[];
  isStart: boolean;
  onUpdate: (patch: Partial<BotNode>) => void;
  onDelete: () => void;
  onSetStart: () => void;
}): JSX.Element {
  return (
    <div className="inbx-flow__form">
      <div className="inbx-flow__form-row">
        <label>ID узла</label>
        <input value={id} disabled />
      </div>

      <div className="inbx-flow__form-row">
        <label>Тип</label>
        <select value={node.type} onChange={(e) => onUpdate({ type: e.target.value as NodeType })}>
          <option value="message">message — сообщение</option>
          <option value="input">input — ввод от пользователя</option>
          <option value="buttons">buttons — кнопки</option>
          <option value="condition">condition — условие</option>
          <option value="api_call">api_call — внешний API</option>
          <option value="transfer">transfer — оператору</option>
          <option value="end">end — завершить</option>
        </select>
      </div>

      {(node.type === 'message' || node.type === 'input' || node.type === 'buttons') && (
        <div className="inbx-flow__form-row">
          <label>Текст</label>
          <textarea
            value={node.text ?? ''}
            onChange={(e) => onUpdate({ text: e.target.value })}
            rows={3}
          />
        </div>
      )}

      {(node.type === 'input' || node.type === 'buttons') && (
        <div className="inbx-flow__form-row">
          <label>Сохранить в переменную</label>
          <input
            value={node.save_as ?? ''}
            onChange={(e) => onUpdate({ save_as: e.target.value })}
          />
        </div>
      )}

      {node.type === 'buttons' && (
        <div className="inbx-flow__form-row">
          <label>Кнопки (по одной в строке: label|value|next_node)</label>
          <textarea
            value={(node.buttons ?? [])
              .map((b) => `${b.label}|${b.value}|${b.next ?? ''}`)
              .join('\n')}
            onChange={(e) =>
              onUpdate({
                buttons: e.target.value
                  .split('\n')
                  .filter(Boolean)
                  .map((line) => {
                    const [label = '', value = '', next] = line.split('|');
                    return { label, value, next: next || undefined };
                  }),
              })
            }
            rows={4}
          />
        </div>
      )}

      {node.type === 'condition' && (
        <>
          <div className="inbx-flow__form-row">
            <label>Переменная</label>
            <input
              value={node.if?.var ?? ''}
              onChange={(e) =>
                onUpdate({ if: { ...(node.if ?? { var: '', op: 'eq' }), var: e.target.value } })
              }
            />
          </div>
          <div className="inbx-flow__form-row">
            <label>Оператор</label>
            <select
              value={node.if?.op ?? 'eq'}
              onChange={(e) =>
                onUpdate({ if: { ...(node.if ?? { var: '', op: 'eq' }), op: e.target.value } })
              }
            >
              <option value="eq">=</option>
              <option value="neq">≠</option>
              <option value="contains">contains</option>
              <option value="gt">&gt;</option>
              <option value="lt">&lt;</option>
              <option value="regex">regex</option>
            </select>
          </div>
          <div className="inbx-flow__form-row">
            <label>Значение</label>
            <input
              value={String(node.if?.val ?? '')}
              onChange={(e) =>
                onUpdate({ if: { ...(node.if ?? { var: '', op: 'eq' }), val: e.target.value } })
              }
            />
          </div>
          <div className="inbx-flow__form-row">
            <label>Then →</label>
            <NextSelect
              value={node.then}
              options={allNodes}
              onChange={(v) => onUpdate({ then: v })}
            />
          </div>
          <div className="inbx-flow__form-row">
            <label>Else →</label>
            <NextSelect
              value={node.else}
              options={allNodes}
              onChange={(v) => onUpdate({ else: v })}
            />
          </div>
        </>
      )}

      {node.type === 'api_call' && (
        <>
          <div className="inbx-flow__form-row">
            <label>Method</label>
            <select
              value={(node.method as string) ?? 'GET'}
              onChange={(e) => onUpdate({ method: e.target.value })}
            >
              <option>GET</option>
              <option>POST</option>
              <option>PUT</option>
              <option>PATCH</option>
              <option>DELETE</option>
            </select>
          </div>
          <div className="inbx-flow__form-row">
            <label>
              URL (поддерживает <code>{'{{vars}}'}</code>)
            </label>
            <input
              value={(node.url as string) ?? ''}
              onChange={(e) => onUpdate({ url: e.target.value })}
            />
          </div>
        </>
      )}

      {node.type !== 'condition' && node.type !== 'end' && (
        <div className="inbx-flow__form-row">
          <label>Next →</label>
          <NextSelect
            value={node.next}
            options={allNodes}
            onChange={(v) => onUpdate({ next: v })}
          />
        </div>
      )}

      <div className="inbx-flow__form-actions">
        {!isStart && (
          <button type="button" onClick={onSetStart}>
            Сделать стартовым
          </button>
        )}
        <button type="button" onClick={onDelete} className="inbx-flow__delete" disabled={isStart}>
          Удалить узел
        </button>
      </div>
    </div>
  );
}

function NextSelect({
  value,
  options,
  onChange,
}: {
  value?: string;
  options: string[];
  onChange: (v: string | undefined) => void;
}): JSX.Element {
  return (
    <select value={value ?? ''} onChange={(e) => onChange(e.target.value || undefined)}>
      <option value="">— конец —</option>
      {options.map((id) => (
        <option key={id} value={id}>
          {id}
        </option>
      ))}
    </select>
  );
}
